// TODO: Overhaul — delete this file (and its consumers/tests) once the consumer InvestmentAccount
// endpoints are wired (#378 live-migration).
import Decimal from 'decimal.js-light';
import { Client } from 'openapi-fetch';

import {
  mockDelay as delay,
  ok,
  paginate,
  rampedAmount,
  randomLatencyMs,
} from '@views/investor/queries/overhaul-mock.util';

import {
  SquirrelMoneyCoreContractsCreateInvestmentAccountForm as CreateInvestmentAccountForm,
  SquirrelMoneyCoreContractsInvestmentAccountDto as InvestmentAccountDto,
  SquirrelMoneyContractsEnumerationsInvestmentAccountStatuses as InvestmentAccountStatuses,
  SquirrelMoneyCoreEnumerationsInvestmentAccountTypes as InvestmentAccountTypes,
  SquirrelMoneyContractsTypesInvestmentClasses as InvestmentClasses,
  SquirrelMoneyApiContractsPaginationPagedList_1SquirrelMoneyCoreContractsInvestmentAccountDtoSquirrelMoneyCoreVersion_1_0_0_0CultureNeutralPublicKeyTokenNull as PagedInvestmentAccounts,
  SquirrelMoneyCoreContractsUpdateInvestmentAccountForm as UpdateInvestmentAccountForm,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';
import { paths as api1Paths } from '@core/schemas/api1-schema';
import { paths as api2Paths } from '@core/schemas/api2-schema';
import { isFeatureEnabled } from '@core/utilities/feature-flags';

import type { BalanceWithMoveable } from './investment-accounts.query';

/**
 * In-memory fixture for the consumer InvestmentAccount endpoints, until the overhaul backend is reachable
 * in the target environment. Lets the account-detail, wallet, home, investments and create screens
 * build against a realistic backend ahead of the API — including the Wallet (account-type 1) and
 * create / update / close / reopen transitions. Reached only through the flag-gated lazy routes, so it
 * ships in a route-level lazy chunk that prod never fetches (the `accountOverhaul` flag is off). Deleted
 * at wire-up — see the live-migration note in `investment-accounts.query.ts`.
 */

/**
 * The backend recomputes the `readonly` derived flags (`canBeClosed` etc.) on every write, so the store
 * holds a mutable variant and re-derives them on close/reopen — the DTO's `readonly` only guards the
 * response shape consumers see, not the server's own bookkeeping.
 */
type MutableAccount = { -readonly [K in keyof InvestmentAccountDto]: InvestmentAccountDto[K] };

/**
 * The store's account: the DTO plus a mock-only pending-investment balance. `InvestmentAccountDto` carries
 * no pending figure (it lives on the balance breakdown), so the mock tracks it on the account and
 * `balanceFor` surfaces it on the returned `InvestmentAccountBalanceDto`.
 * TODO: Overhaul — drop once the real balance breakdown drives pending investment (#441/#443).
 */
type MockAccount = MutableAccount & {
  pendingInvestmentBalance?: number;
  /** When the pending balance started (drives the gradual-investment ramp); defaults to `createDateNzt`. */
  pendingInvestmentSince?: string;
};

/** Account types that invest deposited funds — a move into them arrives as pending investment, then ramps in. */
const investsFunds = (type: InvestmentAccountTypes | undefined): boolean =>
  type === InvestmentAccountTypes.TermInvestment || type === InvestmentAccountTypes.ManagedFund;

/**
 * Whether a move is a same-investment-class transfer between two Term Investments. Its invested funds move
 * DIRECTLY to the destination (no secondary-market sale) — mirroring the flow's `moveSplit` — so there's never a
 * sale to stop. Only Term Investment holdings transfer this way (managed-fund / cash invested balances don't).
 */
const isSameClassTransfer = (from: MockAccount | null, to: MockAccount | null): boolean =>
  from != null &&
  to != null &&
  from.investmentAccountType === InvestmentAccountTypes.TermInvestment &&
  to.investmentAccountType === InvestmentAccountTypes.TermInvestment &&
  from.investmentClass != null &&
  from.investmentClass === to.investmentClass;

/** Recompute an account's total from its buckets (cash + invested + pending) after a balance mutation. */
function recomputeClosingBalance(account: MockAccount): void {
  account.closingBalance = new Decimal(account.cashBalance ?? 0)
    .add(account.investedBalance ?? 0)
    .add(account.pendingInvestmentBalance ?? 0)
    .toNumber();
}

/**
 * Whether an account can be closed: an empty, open account that isn't the Wallet or a Managed Fund.
 * TODO: Overhaul — the backend owns `canBeClosed`; confirm it applies the same rule once wired (#456).
 */
function isClosable(
  account: Pick<MockAccount, 'investmentAccountType' | 'currentStatus'>,
  totalBalance: number | undefined,
) {
  return (
    account.currentStatus === InvestmentAccountStatuses.Active &&
    account.investmentAccountType !== InvestmentAccountTypes.Wallet &&
    account.investmentAccountType !== InvestmentAccountTypes.ManagedFund &&
    totalBalance === 0
  );
}

/** Fixture factory for a mock account. Balances default to 0 unless overridden. */
export function createInvestmentAccountMock(overrides: Partial<MockAccount> = {}): MockAccount {
  const investmentAccountId = overrides.investmentAccountId ?? 1;
  const investmentAccountType = overrides.investmentAccountType ?? InvestmentAccountTypes.TermInvestment;
  const currentStatus = overrides.currentStatus ?? InvestmentAccountStatuses.Active;
  const isWallet = investmentAccountType === InvestmentAccountTypes.Wallet;
  const cashBalance = overrides.cashBalance ?? 0;
  const investedBalance = overrides.investedBalance ?? 0;
  const pendingInvestmentBalance = overrides.pendingInvestmentBalance ?? 0;
  const closingBalance = new Decimal(cashBalance).add(investedBalance).add(pendingInvestmentBalance).toNumber();
  return {
    investmentAccountId,
    displayName: `Account ${investmentAccountId}`,
    investorEntityId: 1,
    investmentAccountGroupId: null,
    investmentAccountGroupName: null,
    sortOrder: investmentAccountId,
    maxSortOrder: null,
    investmentAccountType,
    accruesInterestOnCash: isWallet,
    payInterestToWallet: null,
    payPrincipalToWallet: null,
    currentStatus,
    canBeClosed: isClosable({ investmentAccountType, currentStatus }, closingBalance),
    canBeReopened: currentStatus === InvestmentAccountStatuses.Closed,
    canBeGrouped: false,
    cashBalance,
    investedBalance,
    interestEarned: 0,
    // Balance includes funds pending investment (cash + invested + pending), e.g. $10,000 + $1,000 = $11,000.
    closingBalance,
    createDateNzt: new Date().toISOString(),
    ...overrides,
  };
}

/**
 * A secondary-market sale in progress from a move. As it sells over time (ramped from `createDateNzt` — the SAME
 * ramp the move-underway panel uses), {@link settleSales} moves the sold-so-far off the source's invested balance
 * and onto the destination, so money is only ever in one place. Cancelling a move freezes the sold-so-far, so the
 * unsold remainder stays invested in the source — this is what makes "stop moving funds" leave the unmoved funds
 * behind. Exported for the co-located spec.
 * TODO: Overhaul — mock stand-in; the real backend reports move progress and settles balances itself.
 */
export interface InFlightSale {
  moveId: number;
  fromInvestmentAccountId: number;
  toInvestmentAccountId: number;
  saleAmount: number;
  createDateNzt: string;
  /** How much of the sale has already been settled into the stored balances (advances as it sells). */
  settledSoldAmount?: number;
  /** Set on cancel: freezes the sold-so-far so the unsold remainder stays in the source for good. */
  frozenSoldAmount?: number;
}

/** How much of a sale has sold & moved so far — frozen once cancelled, else ramped over time (capped at the sale). */
export function saleSoldSoFar(sale: InFlightSale, now: number): number {
  if (sale.frozenSoldAmount !== undefined) return sale.frozenSoldAmount;
  return Math.min(sale.saleAmount, rampedAmount(sale.saleAmount, sale.createDateNzt, now));
}

/**
 * Settle each in-flight sale's newly-sold funds into the STORED balances, so a sold amount is only ever in one
 * place. For each sale, move the increment since it was last settled off the source's invested balance and onto
 * the destination — as pending investment (an investing account, where it then ramps into invested like any
 * deposit), as cash (a cash account), or as a Wallet-in delta (the Wallet, which is re-synced from on-call). A
 * fully-sold sale is dropped; a frozen (cancelled) sale settles up to its frozen amount and is then dropped,
 * leaving the unsold remainder invested in the source. A sale whose accounts aren't in the store yet is kept for
 * a later read. Run on every read (see `load`). Exported for the co-located spec.
 */
export function settleSales(store: EntityStore, now: number): void {
  const remaining: InFlightSale[] = [];
  for (const sale of store.sales) {
    const from = store.accounts.find(a => a.investmentAccountId === sale.fromInvestmentAccountId) ?? null;
    const to = store.accounts.find(a => a.investmentAccountId === sale.toInvestmentAccountId) ?? null;
    if (!from || !to) {
      remaining.push(sale); // can't settle until both accounts exist; try again next read
      continue;
    }
    const target = saleSoldSoFar(sale, now);
    const delta = new Decimal(target).sub(sale.settledSoldAmount ?? 0).toNumber();
    if (delta > 0) {
      from.investedBalance = new Decimal(from.investedBalance ?? 0).sub(delta).toNumber();
      recomputeClosingBalance(from);
      if (to.investmentAccountType === InvestmentAccountTypes.Wallet) {
        // The Wallet is re-synced from on-call each read; a delta keeps the arrival reflected (see walletMovedIn).
        store.walletMovedIn = new Decimal(store.walletMovedIn).add(delta).toNumber();
      } else if (investsFunds(to.investmentAccountType)) {
        to.pendingInvestmentBalance = new Decimal(to.pendingInvestmentBalance ?? 0).add(delta).toNumber();
        to.pendingInvestmentSince ??= new Date(now).toISOString();
        recomputeClosingBalance(to);
      } else {
        to.cashBalance = new Decimal(to.cashBalance ?? 0).add(delta).toNumber();
        recomputeClosingBalance(to);
      }
      sale.settledSoldAmount = target;
    }
    // Keep the sale only while more can still settle: an active sale until fully sold, a frozen one until its cap.
    const cap = sale.frozenSoldAmount ?? sale.saleAmount;
    if ((sale.settledSoldAmount ?? 0) < cap) remaining.push(sale);
  }
  store.sales = remaining;
}

/**
 * Derive a balance breakdown from an account (the shape the `/balance` and `/summary` routes return). Funds
 * pending investment invest in equal chunks over time (see {@link rampedAmount}): at `now` some of the original
 * pending has shifted into `investedBalance`, the rest is still pending — the total is unchanged. In-flight move
 * sales are already reflected in the stored balances by {@link settleSales} (run on read before this), so no sale
 * adjustment is applied here.
 */
export function balanceFor(account: MockAccount, now: number): BalanceWithMoveable {
  const cashBalance = account.cashBalance ?? 0;
  const pendingTotal = account.pendingInvestmentBalance ?? 0;
  // Start the ramp the first time the account is read with funds pending, so it invests from when you open
  // the account (not from when it was derived at Home-load). A move deposit sets this explicitly to its time.
  if (pendingTotal > 0) account.pendingInvestmentSince ??= new Date(now).toISOString();
  const investedFromPending = rampedAmount(pendingTotal, account.pendingInvestmentSince, now);
  const pendingInvestmentBalance = new Decimal(pendingTotal).sub(investedFromPending).toNumber();
  const investedBalance = new Decimal(account.investedBalance ?? 0).add(investedFromPending).toNumber();

  // The split a Move + Invest draws on. `moveableCash` moves immediately: idle cash PLUS funds still awaiting
  // investment (the post-ramp `pendingInvestmentBalance`). Pending funds haven't been lent out yet, so moving
  // them out needs no secondary-market sale — the breakdown step labels this portion "Funds awaiting
  // investment". `moveableInvestments` is the invested balance — funds already deployed, so moving them needs a
  // sale (or a direct transfer when the destination holds the same investment class). Only Term Investment
  // holdings are moveable that way, so managed-fund and cash-account invested balances are excluded. (The real
  // backend also excludes individual term investments that can't be sold — TODO: Overhaul — model that finer
  // exclusion once the shape is confirmed; today all of a term account's invested balance is treated as moveable.)
  const moveableCash = new Decimal(cashBalance).add(pendingInvestmentBalance).toNumber();
  const moveableInvestments =
    account.investmentAccountType === InvestmentAccountTypes.TermInvestment ? investedBalance : 0;
  return {
    cashBalance,
    reservedCashBalance: 0,
    availableBalance: cashBalance,
    pendingInvestmentBalance,
    investedBalance,
    totalBalance: new Decimal(cashBalance).add(investedBalance).add(pendingInvestmentBalance).toNumber(),
    movableBalance: new Decimal(moveableCash).add(moveableInvestments).toNumber(),
    moveableCash,
    moveableInvestments,
  };
}

/**
 * The pre-cutover on-call account every entity carries: type Legacy, always closed, holding the transaction
 * history from before the overhaul cutover. Closed → it never shows as a Home card (Home lists only non-closed
 * accounts, and the card builder skips the Legacy type anyway); it exists for reporting purposes to show
 * past transactions.
 */
function createLegacyOnCallMock(investmentAccountId: number): InvestmentAccountDto {
  return createInvestmentAccountMock({
    investmentAccountId,
    investmentAccountType: InvestmentAccountTypes.Legacy,
    displayName: 'Legacy on-call',
    currentStatus: InvestmentAccountStatuses.Closed,
  });
}

/**
 * Seed one account of each type so consuming screens exercise every branch: the Wallet (type 1, never
 * closable), an open term investment, an open on-call (cash) account, an open managed fund, a closed term
 * investment (so "active accounts", the account count, and reopen all have something to act on), and the
 * closed Legacy on-call every user carries.
 */
function seedAccounts(): MockAccount[] {
  return [
    createInvestmentAccountMock({
      investmentAccountId: 1,
      investmentAccountType: InvestmentAccountTypes.Wallet,
      cashBalance: 3_500,
    }),
    createInvestmentAccountMock({
      investmentAccountId: 2,
      investmentAccountType: InvestmentAccountTypes.TermInvestment,
      investmentClass: InvestmentClasses.ResidentialMortgage,
      investedBalance: 25_000,
      interestEarned: 1_240.55,
    }),
    createInvestmentAccountMock({
      investmentAccountId: 3,
      investmentAccountType: InvestmentAccountTypes.Cash,
      cashBalance: 8_000,
      interestEarned: 96.2,
    }),
    createInvestmentAccountMock({
      investmentAccountId: 4,
      investmentAccountType: InvestmentAccountTypes.ManagedFund,
      investedBalance: 12_000,
      interestEarned: 430.1,
    }),
    createInvestmentAccountMock({
      investmentAccountId: 5,
      investmentAccountType: InvestmentAccountTypes.TermInvestment,
      investmentClass: InvestmentClasses.ResidentialMortgage,
      currentStatus: InvestmentAccountStatuses.Closed,
    }),
    createLegacyOnCallMock(6),
  ];
}

/** The base accounts every entity carries — the Wallet (type 1) and the closed Legacy on-call — before the
 * investment accounts are derived from the entity's real investments (see `deriveAccountsFromInvestments`).
 * The Wallet's balance is synced from the on-call account. */
function seedBaseAccounts(): MockAccount[] {
  return [
    createInvestmentAccountMock({ investmentAccountId: 1, investmentAccountType: InvestmentAccountTypes.Wallet }),
    createLegacyOnCallMock(2),
  ];
}

/**
 * Display name for a derived Term Investment account, named after its investment class — matching the
 * consumer-facing fund names used across the account-overhaul flows.
 */
const INVESTMENT_CLASS_NAMES: Record<InvestmentClasses, string> = {
  [InvestmentClasses.ResidentialMortgage]: 'Retirement',
  [InvestmentClasses.CommercialMortgage]: 'Rainy day',
  [InvestmentClasses.ConsumerLoan]: 'Holidays',
  [InvestmentClasses.SpecialisedLoan]: 'University Fund',
};

/** Mocked balance for the interim on-call (Cash) account — its own funds, independent of the Wallet. */
const MOCK_ON_CALL_CASH_BALANCE = 8_000;

interface GetInit {
  params?: { path?: { investorEntityId?: number; investmentAccountId?: number } };
}
interface PostInit {
  params?: { path?: { investorEntityId?: number; investmentAccountId?: number } };
  body?: CreateInvestmentAccountForm;
}
interface PutInit {
  params?: { path?: { investorEntityId?: number; investmentAccountId?: number } };
  body?: UpdateInvestmentAccountForm;
}

/**
 * A mock `ACCOUNT_OVERHAUL_CLIENT` fragment serving the consumer InvestmentAccount endpoints from a
 * **per-entity** in-memory store (keyed by the request path's `investorEntityId`), so list / detail / summary
 * reads and create / update / close / reopen transitions behave like the real backend and persist across
 * screens. Composed into the single root-singleton overhaul seam (`createOverhaulMockClient`); it is not
 * provided directly by screens.
 *
 * With an `api1` client the investment accounts are DERIVED from the entity's real (legacy) investments — one
 * Term Investment account per offered product (Home Loan / Construction Loan), its balance the sum of that
 * product's investments — plus an interim on-call (Cash) account with its own balance and a mocked managed
 * fund. With an `api2` client the Wallet's balance and current-FY net interest are pulled from the legacy
 * on-call endpoint. Without `api1` (unit tests, the create flow) the fixed rich
 * {@link seedAccounts} stands. Every call waits a random 500ms–1500ms to mimic fetch latency; pass
 * `delayMs: 0` in tests.
 */
export interface EntityStore {
  accounts: MockAccount[];
  derivePromise: Promise<void> | null;
  /**
   * Whether the entity is enrolled in managed funds — read from the on-call response's nested entity
   * (`entity.inManagedFund`, the same flag the legacy screens use). Drives the managed-fund account (added by
   * `ensureManagedFundAccount` once enrolment is known), so it reflects the entity's REAL holdings, not the
   * incidental presence of term investments.
   */
  inManagedFund: boolean;
  /**
   * Guards the one-off managed-fund-account add. Set only once enrolment is confirmed AND the account is added, so
   * a failed first on-call read (enrolment still unknown) can't permanently suppress the account — a later
   * successful read reconciles it. Also readies the future MF-registration flow, where enrolment flips on mid-session.
   */
  ensureMfPromise: Promise<void> | null;
  /**
   * Cumulative cash moved OUT of the Wallet by mocked moves. The Wallet's balance is re-read from the (static)
   * on-call endpoint on every fetch, so this delta is subtracted after that re-sync to keep a move reflected.
   */
  walletMovedOut: number;
  /**
   * Cumulative funds moved INTO the Wallet by mocked moves (immediate portions + settled sale proceeds). Added
   * back after the on-call re-sync (which can't know about mocked moves), the mirror of {@link walletMovedOut}.
   * Non-Wallet destinations need no delta — they aren't re-synced, so a move persists on them in place.
   */
  walletMovedIn: number;
  /** Secondary-market sales in progress from moves, settled into the stored balances over time by {@link settleSales}. */
  sales: InFlightSale[];
}

/** How an applied move splits between the immediate portion and the secondary-market sale — the seam writes it
 * onto the created move so the "stop moving funds" summary can read it (the consumer `MoveDto` lacks the split). */
export interface AppliedMoveSplit {
  /** Funds awaiting investment that moved immediately (no sale). */
  immediateAmount: number;
  /** Invested funds put up for sale on the secondary market to fulfil the move. */
  saleAmount: number;
}

/** Applies a created move to an entity's store — the immediate portion moves now, the sale migrates over
 * time. See {@link applyMove}. */
export type ApplyMove = (
  entityId: number,
  fromInvestmentAccountId: number,
  toInvestmentAccountId: number,
  amount: number,
  moveId: number,
) => AppliedMoveSplit;

/** Stops a move's in-flight sale: freezes the sold-so-far, so the unsold funds remain in the source. Called by
 * the overhaul seam after a cancel POST. See {@link InFlightSale}. */
export type StopMoveSale = (entityId: number, moveId: number) => void;

/**
 * Resolves an account's display name from an entity's store — the overhaul seam calls it after a move POST to
 * give the created move its real source/destination name (the moves mock only stubs `Account {id}`). Undefined
 * when the store isn't loaded yet or holds no such account (the seam then keeps the moves mock's stub).
 */
export type AccountNameOf = (entityId: number, investmentAccountId: number) => string | undefined;

/** Resolves an account from an entity's (loaded) store — the transactions mock uses it to pick each account's
 * source: the on-call bank account for the Wallet, the investments of its class for a Term Investment. */
export type AccountOf = (entityId: number, investmentAccountId: number) => Promise<InvestmentAccountDto | null>;

/** Resolves an entity's (loaded) accounts — the investments mock uses it to assign each legacy investment to the
 * Term Investment account derived for its class. */
export type AccountsOf = (entityId: number) => Promise<InvestmentAccountDto[]>;

/** The accounts mock client plus the hooks the overhaul seam calls after a move POST ({@link ApplyMove}). */
export type InvestmentAccountsMockClient = Client<accountOverhaulPaths> & {
  applyMove: ApplyMove;
  stopMoveSale: StopMoveSale;
  accountNameOf: AccountNameOf;
  accountOf: AccountOf;
  accountsOf: AccountsOf;
};

export function createInvestmentAccountsMockClient(
  options: { delayMs?: number; api1?: Client<api1Paths>; api2?: Client<api2Paths> } = {},
): InvestmentAccountsMockClient {
  const { delayMs, api1, api2 } = options;
  const wait = () => delay(delayMs ?? randomLatencyMs());

  // Per-entity backend state, seeded on first touch and kept for the life of the client, so a create/close on
  // one screen is visible on the next (the client is a root singleton). With `api1` the accounts are DERIVED
  // from the entity's real (legacy) investments (base Wallet + Legacy on-call, then a Term Investment per class
  // held, an interim on-call, a managed fund when enrolled); without it (unit tests / the create flow) the fixed rich
  // {@link seedAccounts} stands. `api2` syncs the Wallet balance + current-FY interest from the on-call endpoint.
  const stores = new Map<number, EntityStore>();
  const storeFor = (entityId: number): EntityStore => {
    let store = stores.get(entityId);
    if (!store) {
      store = {
        accounts: api1 ? seedBaseAccounts() : seedAccounts(),
        derivePromise: null,
        inManagedFund: false,
        ensureMfPromise: null,
        walletMovedOut: 0,
        walletMovedIn: 0,
        sales: [],
      };
      stores.set(entityId, store);
    }
    return store;
  };
  const findIn = (accounts: MockAccount[], id?: number) =>
    accounts.find(account => account.investmentAccountId === id) ?? null;
  const walletOf = (accounts: MockAccount[]) =>
    accounts.find(account => account.investmentAccountType === InvestmentAccountTypes.Wallet) ?? null;
  const nextIdIn = (accounts: MockAccount[]): number =>
    accounts.reduce((max, a) => Math.max(max, a.investmentAccountId ?? 0), 0) + 1;

  // Pull the live on-call figures into the entity's Wallet: the available balance and — standing in for the
  // not-yet-consumer-exposed all-time `interestEarned` (gap G16) — the current-FY net interest. Gated on the
  // overhaul flag (mirroring the real client); without an `api2` client (e.g. unit tests) it's a no-op.
  const syncWalletFromOnCall = async (entityId: number, store: EntityStore): Promise<void> => {
    const { accounts } = store;
    const w = walletOf(accounts);
    if (!api2 || w === null || !isFeatureEnabled('accountOverhaul')) return;
    const { data, error } = await api2.GET('/v2/on-call-accounts/{entityId}', {
      params: { path: { entityId }, query: { pageIndex: 0, pageSize: 10 } },
    });
    if (error) return;
    // The on-call read nests the investor entity, which carries the managed-fund enrolment flag — capture it so
    // `ensureManagedFundAccount` can add the managed-fund account from real holdings.
    store.inManagedFund = data?.entity?.inManagedFund ?? false;
    const balance = data?.entityStatistics?.fundsAvailableForInvesting;
    if (balance != null) {
      // Re-apply the mocked moves the on-call read can't know about, so they stay reflected: subtract what's moved
      // out of the Wallet, add what's moved in (immediate portions + settled sale proceeds). Only the Wallet is
      // synced from on-call; the interim Cash account is separate, so a move into it persists in place instead.
      const available = new Decimal(balance).sub(store.walletMovedOut).add(store.walletMovedIn).toNumber();
      w.cashBalance = available;
      w.closingBalance = new Decimal(w.cashBalance ?? 0).add(w.investedBalance ?? 0).toNumber();
    }
    const interest = data?.netInterestReceivedCurrentFinancialYear;
    if (interest != null) w.interestEarned = interest;
  };

  // Derive the entity's investment accounts from its real (legacy) investments: one Term Investment account per
  // class held (balance = the sum of that class's investments' closing balances) and an interim on-call (Cash)
  // account when the Wallet has a non-$0 balance. The managed-fund account is handled separately by
  // `ensureManagedFundAccount` — it depends on enrolment, which can resolve after this one-off derivation. Memoised
  // per entity (runs once, after the wallet sync). A no-op without `api1`.
  const deriveAccountsFromInvestments = (entityId: number, store: EntityStore): Promise<void> => {
    if (!api1 || !isFeatureEnabled('accountOverhaul')) return Promise.resolve();
    return (store.derivePromise ??= (async () => {
      const { data, error } = await api1.GET('/v2/investor-entities/{investorEntityId}/investments', {
        params: { path: { investorEntityId: entityId }, query: { activeOnly: true, pageIndex: 0, pageSize: 500 } },
      });
      if (error) return;
      const balanceByClass = new Map<InvestmentClasses, number>();
      for (const investment of data?.investments?.items ?? []) {
        const investmentClass = investment.investmentClass;
        if (investmentClass == null) continue;
        balanceByClass.set(
          investmentClass,
          new Decimal(balanceByClass.get(investmentClass) ?? 0).add(investment.closingBalance ?? 0).toNumber(),
        );
      }
      for (const [investmentClass, investedBalance] of balanceByClass) {
        store.accounts.push(
          createInvestmentAccountMock({
            investmentAccountId: nextIdIn(store.accounts),
            investmentAccountType: InvestmentAccountTypes.TermInvestment,
            investmentClass,
            investedBalance,
            displayName: INVESTMENT_CLASS_NAMES[investmentClass] ?? 'Term Investments',
          }),
        );
      }
      const walletBalance = walletOf(store.accounts)?.cashBalance ?? 0;
      if (walletBalance > 0) {
        store.accounts.push(
          createInvestmentAccountMock({
            investmentAccountId: nextIdIn(store.accounts),
            investmentAccountType: InvestmentAccountTypes.Cash,
            displayName: 'Liquid savings',
            cashBalance: MOCK_ON_CALL_CASH_BALANCE,
          }),
        );
      }
    })());
  };

  // Synthesise the managed-fund account for an enrolled entity. Enrolment (`inManagedFund`) means the account
  // exists on the accounts endpoint — which, on the real backend, is SEPARATE from and independent of the
  // valuations read. So the account is ensured up front for an enrolled entity, then its balance is reconciled
  // from the entity's REAL valuations (the same legacy endpoint the legacy Managed Fund screen reads): balance =
  // the sum of the holdings' values, interest earned = the all-time net return. Created with a $0 "setting up"
  // balance when enrolment first surfaces (a mid-session MF registration), then reconciled as valuations land. A
  // valuations ERROR leaves the account IN PLACE with its balance unreconciled (NOT dropped) — the consumer
  // surfaces the valuations failure inline (the summary tiles show a placeholder), so the account mustn't vanish
  // from the list. On a valuations failure DON'T retry: the in-flight promise is cleared
  // ONLY on success, so an error leaves it set and later loads return without re-fetching. A no-op without `api1`,
  // off-flag, or when the entity isn't enrolled. (The create POST shares the one-account check, so this derivation
  // and an explicit create can't double up.)
  const ensureManagedFundAccount = (entityId: number, store: EntityStore): Promise<void> => {
    if (!api1 || !isFeatureEnabled('accountOverhaul') || !store.inManagedFund) return Promise.resolve();
    return (store.ensureMfPromise ??= (async () => {
      const account =
        store.accounts.find(a => a.investmentAccountType === InvestmentAccountTypes.ManagedFund) ??
        store.accounts[
          store.accounts.push(
            createInvestmentAccountMock({
              investmentAccountId: nextIdIn(store.accounts),
              investmentAccountType: InvestmentAccountTypes.ManagedFund,
              displayName: 'I love PIE',
              investedBalance: 0,
              interestEarned: 0,
            }),
          ) - 1
        ];
      try {
        const { data: managedFundData, error } = await api1.GET(
          '/v2/investor-entities/{investorEntityId}/managed-funds/valuations',
          { params: { path: { investorEntityId: entityId } } },
        );
        // A valuations error (or a 200 with empty/null valuations — the not-yet-ready path) leaves the account at
        // its current balance: the $0 "setting up" figure on first load, or the last reconciled figure after. On
        // error the memo stays (no retry); on a 200 it's cleared below so the next load reconciles.
        if (error) return;
        const managedFundBalance = (managedFundData?.valuations ?? []).reduce(
          (total, valuation) => new Decimal(total).add(valuation.value ?? 0).toNumber(),
          0,
        );
        account.investedBalance = managedFundBalance;
        account.interestEarned = managedFundData?.returns?.allTime?.returnAmount ?? 0;
        account.closingBalance = new Decimal(account.cashBalance ?? 0)
          .add(managedFundBalance)
          .add(account.pendingInvestmentBalance ?? 0)
          .toNumber();
        // Clear the memo ONLY on success, so the next load re-reads valuations and reconciles the balance; an
        // error leaves it set (no retry — see above). The upsert is idempotent.
        store.ensureMfPromise = null;
      } catch {
        // Unexpected failure — treat it like a valuations error: account stays, memo stays (no retry).
      }
    })());
  };

  // Seed (if needed) + settle in-flight sales + sync the wallet + derive the entity's store, then return it.
  // Settling runs first so a Wallet-in delta from a just-sold parcel is picked up by the wallet sync this read;
  // it's a no-op on the very first load (no sales yet, and any sale's accounts already exist by the time one is
  // created). The wallet sync runs every read (the balance changes, e.g. after a withdrawal cancel); derive is
  // memoised per entity.
  const load = async (entityId: number): Promise<EntityStore> => {
    const store = storeFor(entityId);
    settleSales(store, Date.now());
    await syncWalletFromOnCall(entityId, store);
    await deriveAccountsFromInvestments(entityId, store);
    await ensureManagedFundAccount(entityId, store);
    return store;
  };

  // Apply a created move to the entity's store, splitting the amount the same way the flow's `moveSplit` presents
  // it. The LIQUID portion (idle cash first, then funds awaiting investment) moves now and lands as pending
  // investment (or cash) at the destination. What's beyond the liquid funds comes from invested holdings and goes
  // one of two ways:
  //  - Same investment class (a direct transfer): the invested funds move straight across and land INVESTED at the
  //    destination — no secondary-market sale, so there's nothing to stop and no "move underway" panel.
  //  - Different class: it's sold on the secondary market — recorded as an in-flight sale that `settleSales`
  //    migrates to the destination as it sells, leaving the unsold remainder invested in the source (so stopping
  //    the move keeps the unmoved funds here — see {@link stopMoveSale}).
  // The Wallet source is a delta that survives the on-call re-sync ({@link EntityStore.walletMovedOut}); it's all
  // cash, so it's entirely liquid. Realising the pending→invested ramp into the stored figures first keeps the
  // deduction self-consistent. Returns the immediate/sale split (sale 0 for a direct transfer) for the seam.
  const applyMove: ApplyMove = (entityId, fromInvestmentAccountId, toInvestmentAccountId, amount, moveId) => {
    const store = storeFor(entityId);
    const now = Date.now();
    const from = findIn(store.accounts, fromInvestmentAccountId);
    const to = findIn(store.accounts, toInvestmentAccountId);
    // Defaults cover a Wallet / cash source: the whole amount is liquid, nothing sold or transferred directly.
    let split: AppliedMoveSplit = { immediateAmount: amount, saleAmount: 0 };
    let liquidImmediate = amount; // lands as pending investment / cash at the destination
    let directTransfer = 0; // same-class invested that lands already invested at the destination
    if (from?.investmentAccountType === InvestmentAccountTypes.Wallet) {
      store.walletMovedOut = new Decimal(store.walletMovedOut).add(amount).toNumber();
    } else if (from) {
      // Realise the pending→invested ramp so the liquid draw comes off current figures, then restart the clock
      // for whatever pending remains. Invested keeps the sale portion — it only leaves as it sells.
      const pendingTotal = from.pendingInvestmentBalance ?? 0;
      if (pendingTotal > 0) from.pendingInvestmentSince ??= new Date(now).toISOString();
      const investedFromPending = rampedAmount(pendingTotal, from.pendingInvestmentSince, now);
      const realisedPending = new Decimal(pendingTotal).sub(investedFromPending).toNumber();
      const cash = from.cashBalance ?? 0;
      liquidImmediate = Math.min(amount, new Decimal(cash).add(realisedPending).toNumber());
      const fromCash = Math.min(liquidImmediate, cash);
      const fromPending = new Decimal(liquidImmediate).sub(fromCash).toNumber();
      const beyondLiquid = new Decimal(amount).sub(liquidImmediate).toNumber();
      const sameClass = isSameClassTransfer(from, to);
      directTransfer = sameClass ? beyondLiquid : 0;
      const saleAmount = sameClass ? 0 : beyondLiquid;
      // A same-class transfer has no sale, so the whole amount is "immediate"; otherwise only the liquid funds are.
      split = { immediateAmount: sameClass ? amount : liquidImmediate, saleAmount };
      from.cashBalance = new Decimal(cash).sub(fromCash).toNumber();
      from.pendingInvestmentBalance = new Decimal(realisedPending).sub(fromPending).toNumber();
      // The directly-transferred invested leaves now; a sale portion stays invested until it sells (balanceFor).
      from.investedBalance = new Decimal(from.investedBalance ?? 0)
        .add(investedFromPending)
        .sub(directTransfer)
        .toNumber();
      from.pendingInvestmentSince = (from.pendingInvestmentBalance ?? 0) > 0 ? new Date(now).toISOString() : undefined;
      recomputeClosingBalance(from);
      if (saleAmount > 0) {
        store.sales.push({
          moveId,
          fromInvestmentAccountId,
          toInvestmentAccountId,
          saleAmount,
          createDateNzt: new Date(now).toISOString(),
          settledSoldAmount: 0,
        });
      }
    }
    if (to) {
      // The liquid portion arrives as pending investment (an investing account), cash (a cash account), or a
      // Wallet-in delta (the Wallet, re-synced from on-call). A same-class direct transfer's invested funds arrive
      // already invested. A non-same-class sale arrives over time (see settleSales).
      if (to.investmentAccountType === InvestmentAccountTypes.Wallet) {
        store.walletMovedIn = new Decimal(store.walletMovedIn).add(liquidImmediate).toNumber();
      } else if (investsFunds(to.investmentAccountType)) {
        to.pendingInvestmentBalance = new Decimal(to.pendingInvestmentBalance ?? 0).add(liquidImmediate).toNumber();
        to.pendingInvestmentSince = new Date(now).toISOString();
      } else {
        to.cashBalance = new Decimal(to.cashBalance ?? 0).add(liquidImmediate).toNumber();
      }
      if (directTransfer > 0) {
        to.investedBalance = new Decimal(to.investedBalance ?? 0).add(directTransfer).toNumber();
      }
      // The Wallet's closing balance is recomputed by the on-call re-sync; every other destination here.
      if (to.investmentAccountType !== InvestmentAccountTypes.Wallet) recomputeClosingBalance(to);
    }
    return split;
  };

  // Stop a move's in-flight sale: freeze the sold-so-far, so `settleSales` migrates exactly that much and the
  // unsold remainder stays invested in the source. A no-op for an all-immediate move (no sale recorded) or an
  // already-frozen one. Mirrors the "stop moving funds" copy: any unmoved funds remain invested here.
  const stopMoveSale: StopMoveSale = (entityId, moveId) => {
    const sale = storeFor(entityId).sales.find(s => s.moveId === moveId);
    if (sale && sale.frozenSoldAmount === undefined) sale.frozenSoldAmount = saleSoldSoFar(sale, Date.now());
  };

  const accountNameOf: AccountNameOf = (entityId, investmentAccountId) => {
    const name = findIn(storeFor(entityId).accounts, investmentAccountId)?.displayName;
    return name ? name : undefined;
  };

  // Load only when the account isn't in the store yet: a full load re-syncs the Wallet from on-call.
  const accountOf: AccountOf = async (entityId, investmentAccountId) =>
    findIn(storeFor(entityId).accounts, investmentAccountId) ??
    findIn((await load(entityId)).accounts, investmentAccountId);

  const accountsOf: AccountsOf = async entityId => (await load(entityId)).accounts;

  // An empty account can still have money on its way in: a move's secondary-market sale settles into it over time
  // (see settleSales), so it isn't closable until every sale into it has settled — otherwise the proceeds would
  // land in a closed account.
  const canClose = (store: EntityStore, account: MockAccount, now: number): boolean =>
    isClosable(account, balanceFor(account, now).totalBalance) &&
    !store.sales.some(sale => sale.toInvestmentAccountId === account.investmentAccountId);

  // A returned account with its `closingBalance` recomputed from the (settled) stored balances + pending ramp, so
  // the cards/headers that read `closingBalance` agree with the `/balance` breakdown, and `canBeClosed` re-derived
  // live (a move can empty, fund or start paying into an account). Shallow clone so a refetch after a mutation
  // yields fresh object references for the consumer's signal computeds.
  const accountView = (store: EntityStore, account: MockAccount, now: number): MockAccount => ({
    ...account,
    closingBalance: balanceFor(account, now).totalBalance,
    canBeClosed: canClose(store, account, now),
  });

  const client = {
    async GET(path: string, init?: GetInit) {
      await wait();
      const entityId = init?.params?.path?.investorEntityId;
      if (entityId === undefined) return Promise.resolve(ok(null));
      const id = init?.params?.path?.investmentAccountId;
      // `load` settles in-flight sales into the stored balances first, so the reads below are already conserved.
      const store = await load(entityId);
      const { accounts } = store;
      const now = Date.now();

      if (path.endsWith('/summary')) {
        const account = findIn(accounts, id);
        return Promise.resolve(
          ok(
            account ? { investmentAccount: accountView(store, account, now), balance: balanceFor(account, now) } : null,
          ),
        );
      }
      if (path.endsWith('/balance')) {
        const account = findIn(accounts, id);
        return Promise.resolve(ok(account ? balanceFor(account, now) : null));
      }
      if (path.endsWith('/{investmentAccountId}')) {
        const account = findIn(accounts, id);
        return Promise.resolve(ok(account ? accountView(store, account, now) : null));
      }
      // List `/investment-accounts`. Return a fresh snapshot (accounts with live closing balances) each call so a
      // refetch after a mutation (e.g. cancelling a withdrawal, or a move progressing) yields new object
      // references — otherwise the in-place `syncWalletFromOnCall` update wouldn't propagate through the
      // consumer's signal computeds and the displayed balance would stay stale.
      return Promise.resolve(
        ok<PagedInvestmentAccounts>(paginate(accounts.map(account => accountView(store, account, now)))),
      );
    },

    async POST(path: string, init?: PostInit) {
      await wait();
      const entityId = init?.params?.path?.investorEntityId;
      if (entityId === undefined) return Promise.resolve(ok(null));
      const store = storeFor(entityId);
      const { accounts } = store;
      const id = init?.params?.path?.investmentAccountId;

      if (path.endsWith('/close')) {
        const account = findIn(accounts, id);
        // The backend refuses to close an account it reports as not closable, whatever the UI showed.
        if (account && !canClose(store, account, Date.now())) {
          return Promise.resolve({
            data: undefined,
            error: [{ errorMessage: 'This account cannot be closed' }],
            response: new Response(null, { status: 400 }),
          });
        }
        if (account) {
          account.currentStatus = InvestmentAccountStatuses.Closed;
          account.canBeClosed = false;
          account.canBeReopened = true;
        }
        return Promise.resolve(ok(account));
      }
      if (path.endsWith('/reopen')) {
        const account = findIn(accounts, id);
        if (account) {
          account.currentStatus = InvestmentAccountStatuses.Active;
          account.canBeClosed = canClose(store, account, Date.now());
          account.canBeReopened = false;
        }
        return Promise.resolve(ok(account));
      }

      // Create `/investment-accounts`. The typed client declares the body required, so callers always
      // supply a full `CreateInvestmentAccountForm` (the `PostInit.body?` is only loose because this stub
      // hand-rolls its param types).
      const body = init?.body ?? {};
      // One managed fund per entity: if `ensureManagedFundAccount` already derived one from the enrolment signal
      // (a race between the register-success account-list refetch and this create POST), reuse it rather than
      // adding a second — the real BE enforces the one-account limit too. Apply the requested name.
      if (body.investmentAccountType === InvestmentAccountTypes.ManagedFund) {
        const existingManagedFund = accounts.find(
          account => account.investmentAccountType === InvestmentAccountTypes.ManagedFund,
        );
        if (existingManagedFund) {
          if (body.displayName != null) existingManagedFund.displayName = body.displayName;
          return Promise.resolve(ok(existingManagedFund));
        }
      }
      // Only pass through the keys the caller actually sent — spreading an explicit `undefined` would
      // clobber the factory's defaults (e.g. blank out the `Account N` displayName).
      const created = createInvestmentAccountMock({
        investmentAccountId: nextIdIn(accounts),
        investmentAccountType: body.investmentAccountType ?? InvestmentAccountTypes.TermInvestment,
        ...(body.investmentClass != null ? { investmentClass: body.investmentClass } : {}),
        ...(body.displayName != null ? { displayName: body.displayName } : {}),
        payInterestToWallet: body.payInterestToWallet ?? null,
        payPrincipalToWallet: body.payPrincipalToWallet ?? null,
        currentStatus: InvestmentAccountStatuses.Active,
      });
      accounts.push(created);
      return Promise.resolve(ok(created));
    },

    async PUT(path: string, init?: PutInit) {
      await wait();
      const entityId = init?.params?.path?.investorEntityId;
      if (entityId === undefined) return Promise.resolve(ok(null));
      const account = findIn(storeFor(entityId).accounts, init?.params?.path?.investmentAccountId);
      const body = init?.body;
      // Apply exactly the fields the caller sent (key present) — incl. an explicit `null` on the nullable
      // fields to clear them. `accruesInterestOnCash` is a non-nullable bool on the account, so a form
      // `null` there means "no change" and is skipped.
      if (account && body) {
        if (body.displayName !== undefined) account.displayName = body.displayName;
        if (body.accruesInterestOnCash != null) account.accruesInterestOnCash = body.accruesInterestOnCash;
        if (body.payInterestToWallet !== undefined) account.payInterestToWallet = body.payInterestToWallet;
        if (body.payPrincipalToWallet !== undefined) account.payPrincipalToWallet = body.payPrincipalToWallet;
      }
      return Promise.resolve(ok(account));
    },
    // Cast: this stub implements only the InvestmentAccount routes, not the whole overhaul surface.
  } as unknown as Client<accountOverhaulPaths>;

  return Object.assign(client, { applyMove, stopMoveSale, accountNameOf, accountOf, accountsOf });
}
